// src/lib/auth.js
import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  getAuth,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  getIdToken,
  getIdTokenResult,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FB_DATABASE_URL,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
  appId: process.env.REACT_APP_FB_APP_ID,
};

// Warn in dev if env is incomplete (non-blocking)
(function warnMissingEnv(cfg) {
  try {
    const missing = Object.entries(cfg)
      .filter(([, v]) => !v)
      .map(([k]) => k);
    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn(
        "[auth] Missing Firebase env values:",
        missing.join(", "),
        "\nCheck .env (REACT_APP_FB_*) and restart the dev server."
      );
    }
  } catch (e) {
    // ignore
  }
})(firebaseConfig);

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

let authConfigured = false;

function truthyEnv(value) {
  return ["1", "true", "yes", "on"].includes(String(value || "").trim().toLowerCase());
}

function isLocalhost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}

function configureAuth() {
  if (authConfigured) return;

  if (isLocalhost() && truthyEnv(process.env.REACT_APP_USE_AUTH_EMULATOR)) {
    const host = process.env.REACT_APP_AUTH_EMULATOR_HOST || "127.0.0.1";
    const port = Number(process.env.REACT_APP_AUTH_EMULATOR_PORT || 9099);
    connectAuthEmulator(auth, `http://${host}:${port}`, { disableWarnings: true });
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info("[auth] Mode: EMULATOR — auth routed to", `http://${host}:${port}`);
    }
  } else if (process.env.NODE_ENV === "development" && isLocalhost()) {
    // eslint-disable-next-line no-console
    console.info("[auth] Mode: PRODUCTION — connecting to live Firebase Auth");
  }

  authConfigured = true;
}

configureAuth();

// Persist session across reloads
setPersistence(auth, browserLocalPersistence);

export const observeAuth = (cb) => onAuthStateChanged(auth, cb);
export const emailSignIn = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);
export const resetPw = (email) => sendPasswordResetEmail(auth, email);
export const signOutNow = () => signOut(auth);

export const fetchIdToken = async (forceRefresh = false) => {
  const u = auth.currentUser;
  if (!u) return null;
  return getIdToken(u, forceRefresh);
};

export const getAdminAllowlist = () => {
  const raw = process.env.REACT_APP_ADMIN_EMAILS || "";
  return new Set(
    raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
};

export async function getAdminAccess(user, forceRefresh = false) {
  if (!user) {
    return {
      user: null,
      email: "",
      hasAdminClaim: false,
      isAllowlisted: false,
      isAdmin: false,
    };
  }

  const email = String(user.email || "").toLowerCase();
  const tokenResult = await getIdTokenResult(user, forceRefresh);
  const hasAdminClaim = tokenResult?.claims?.admin === true;
  const isAllowlisted = getAdminAllowlist().has(email);

  return {
    user,
    email,
    hasAdminClaim,
    isAllowlisted,
    isAdmin: hasAdminClaim,
  };
}

export const observeAdminAccess = (cb, forceRefresh = true) =>
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      cb({
        user: null,
        email: "",
        hasAdminClaim: false,
        isAllowlisted: false,
        isAdmin: false,
      });
      return;
    }

    try {
      cb(await getAdminAccess(user, forceRefresh));
    } catch {
      cb({
        user,
        email: String(user.email || "").toLowerCase(),
        hasAdminClaim: false,
        isAllowlisted: getAdminAllowlist().has(String(user.email || "").toLowerCase()),
        isAdmin: false,
      });
    }
  });
